import React, { useState, useEffect, useCallback, use } from "react";
import {
  Tabs,
  Card,
  Row,
  Col,
  Select,
  DatePicker,
  Button,
  message,
} from "antd";
import { SearchOutlined } from "@ant-design/icons";
import { useNavigate, useLocation, Navigate } from "react-router-dom";
import dayjs from "dayjs";
import { getProfileTest } from "../../core/api/auth";
import { useSelector } from "react-redux";
import useGlobalFilters from "../../hooks/useGlobalFilters";

// Component imports
import Invoices from "./components/Invoices";
import CreateInvoice from "./components/Shared/Forms/CreateInvoice";
import InvoiceItems from "./components/InvoiceItems";
import PaymentAllocationTab from "./components/PaymentAllocationTab";
import Settings from "./components/Settings/Settings";
import BrandSelector from "../../components/BrandSelector";
import Statements from "./components/Statements";
import VendorInvoiceStatistics from "./components/Reports/VendorInvoiceStatistics";
import ActionsHistory from "./components/Shared/Utils/ActionsHistory";
import CreateCredit from "./components/Shared/Forms/CreateCredit";
import Credits from "./components/Credits";
import AgingReport from "./components/Reports/AgingReport";
import Aspire from "./components/Reports/Aspire";
import FinancialReport from "./components/Reports/FinancialReport";
import AccountingDashboard from "./components/AccountingDashboard";
import ChartOfAccounts from "./components/ChartOfAccounts";
const { Option } = Select;
const { RangePicker } = DatePicker;

const allTabs = [
  // { key: "dashboard", label: "Dashboard", path: "/accounting/dashboard" },
  { key: "invoices", label: "Invoices", path: "/accounting/invoices" },
  {
    key: "create-invoice",
    label: "Create Invoice",
    path: "/accounting/create-invoice",
  },
  {
    key: "payment-allocation",
    label: "Payment Allocation",
    path: "/accounting/payment-allocation",
  },
  { key: "reports", label: "Reports", path: "/accounting/reports" },
  { key: "settings", label: "Settings", path: "/accounting/settings" },
  {
    key: "actions-history",
    label: "Actions History",
    path: "/accounting/actions-history",
  },
  {
    key: "aging-report",
    label: "Aging Report",
    path: "/accounting/aging-report",
  },
  { key: "aspire", label: "Aspire", path: "/accounting/aspire" },
  {
    key: "chart-of-accounts",
    label: "Chart of Accounts",
    path: "/accounting/chart-of-accounts",
  },
];

const AccountingTab = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const profile = useSelector((state) => state.profile);
  const { selectedBrands, brandIds } = useGlobalFilters();

  // Removed excessive logging
  const [filters, setFilters] = useState({
    dateRange: [dayjs().subtract(3, "years"), dayjs()],
    partnerType: "all",
    status: "all",
  });

  const [activeKey, setActiveKey] = useState("invoices");

  // Normalize paths for comparison
  // const normalize = (str) => (str || "").replace(/\/+$/, "").toLowerCase();

  const reportSubPaths = ["vendor-report", "financial-report"];
  const normalize = (str) => (str || "").replace(/\/+$/, "").toLowerCase();

  const paymentSubPaths = [
    "bank-statements",
    "statements-history",
    "credits",
    "cash",
    "create-credit",
    "create-cash",
  ];

  const getActiveKeyFromPath = useCallback(() => {
    const path = normalize(location.pathname);

    // if (path.includes("/accounting/dashboard")) return "dashboard";
    if (path.includes("/accounting/invoices")) return "invoices";
    if (path.includes("/accounting/create-invoice")) return "create-invoice";
    if (path.includes("/accounting/payment-allocation"))
      return "payment-allocation";
    if (path.includes("/accounting/reports")) return "reports";
    if (path.includes("/accounting/settings")) return "settings";
    if (path.includes("/accounting/actions-history")) return "actions-history";
    if (path.includes("/accounting/aging-report")) return "aging-report";
    if (path.includes("/accounting/aspire")) return "aspire";
    if (path.includes("/accounting/chart-of-accounts"))
      return "chart-of-accounts";
    return "invoices";
  }, [location.pathname]);

  useEffect(() => {
    const active = getActiveKeyFromPath();
    setActiveKey(active);
  }, [location.pathname, getActiveKeyFromPath]);

  const onChange = (key) => {
    navigate(`/accounting/${key}`);
    setActiveKey(key);
  };

  const handleDateRangeChange = (dates) =>
    setFilters({ ...filters, dateRange: dates });

  const handlePartnerTypeChange = (value) =>
    setFilters({ ...filters, partnerType: value });

  const handleStatusChange = (value) =>
    setFilters({ ...filters, status: value });

  const applyFilters = () =>
    setFilters({
      ...filters,
      applied: true,
      brands: brandIds,
      timestamp: new Date().getTime(),
    });

  const getIdFromQuery = () => {
    const searchParams = new URLSearchParams(location.search);
    return searchParams.get("id");
  };

  const renderTabContent = () => {
    const id = getIdFromQuery();
    // Use first selected brand for components that only support single brand
    const firstBrand = selectedBrands.length > 0 ? selectedBrands[0] : null;

    switch (activeKey) {
      // case "dashboard":
      //   return <AccountingDashboard />;
      case "invoices":
        return <Invoices filters={filters} selectedBrand={firstBrand} />;
      case "create-invoice":
        return <CreateInvoice selectedBrand={firstBrand} invoiceId={id} />;
      case "credits":
        return <Credits filters={filters} selectedBrand={firstBrand} />;
      case "create-credit":
        return <CreateCredit selectedBrand={firstBrand} creditId={id} />;
      case "invoice-items":
        return <InvoiceItems filters={filters} selectedBrand={firstBrand} />;
      case "statements":
        return <Statements filters={filters} selectedBrand={firstBrand} />;
      case "payment-allocation":
        return (
          <PaymentAllocationTab filters={filters} selectedBrand={firstBrand} />
        );

      case "reports":
        return (
          // <VendorInvoiceStatistics
          //   filters={filters}
          //   selectedBrand={firstBrand}
          // />
          <Tabs type="card" defaultActiveKey="vendor-report" size="small">
            <Tabs.TabPane tab="Vendor Report" key="vendor-report">
              <VendorInvoiceStatistics
                filters={filters}
                selectedBrand={firstBrand}
              />
            </Tabs.TabPane>
            <Tabs.TabPane tab="Financial Report" key="financial-report">
              <FinancialReport
              // filters={filters}
              // selectedBrand={firstBrand}
              />
            </Tabs.TabPane>
          </Tabs>
        );
      case "actions-history":
        return <ActionsHistory selectedBrand={firstBrand} />;
      case "settings":
        return <Settings selectedBrand={firstBrand} />;
      case "aging-report":
        return <AgingReport selectedBrand={firstBrand} filters={filters} />;
      case "aspire":
        return <Aspire selectedBrand={firstBrand} filters={filters} />;
      case "chart-of-accounts":
        return <ChartOfAccounts selectedBrand={firstBrand} />;
      default:
        return <Navigate to="/accounting/dashboard" replace />;
    }
  };

  if (!profile) return null;

  return (
    <div className="page-wrapper">
      <div className="accounting-content">
        <Card title="Accounting" className="accounting-card" size="small">
          <Row gutter={[16, 16]} className="filter-row" align="bottom">
            <Col xs={6} sm={4} md={4} lg={4}>
              <div className="filter-label">Brand:</div>
              {activeKey === "reports" ? (
                <div style={{ paddingTop: 6 }}>
                  {selectedBrands.length > 0
                    ? `${selectedBrands.length} Brand(s) Selected`
                    : "No Brands Selected"}
                </div>
              ) : (
                <div style={{ paddingTop: 6 }}>
                  {selectedBrands.length > 0
                    ? `${selectedBrands.length} Brand(s) Selected`
                    : "No Brands Selected"}
                </div>
              )}
            </Col>
            <Col xs={6} sm={6} md={5} lg={5}>
              <div className="filter-label">Date Range:</div>
              <RangePicker
                value={filters.dateRange}
                onChange={handleDateRangeChange}
                style={{ width: "100%" }}
              />
            </Col>
            <Col xs={4} sm={4} md={4} lg={4}>
              <div className="filter-label">Partner Type:</div>
              <Select
                value={filters.partnerType}
                onChange={handlePartnerTypeChange}
                style={{ width: "100%" }}
              >
                <Option value="all">All</Option>
                <Option value="customer">Customer</Option>
                <Option value="vendor">Vendor</Option>
              </Select>
            </Col>
            <Col xs={4} sm={4} md={4} lg={4}>
              <div className="filter-label">Status:</div>
              <Select
                value={filters.status}
                onChange={handleStatusChange}
                style={{ width: "100%" }}
              >
                <Option value="all">All</Option>
                <Option value="draft">Draft</Option>
                <Option value="sent">Sent</Option>
                <Option value="paid">Paid</Option>
                <Option value="overdue">Overdue</Option>
              </Select>
            </Col>
            <Col xs={4} sm={4} md={3} lg={3}>
              <Button
                type="primary"
                icon={<SearchOutlined />}
                onClick={applyFilters}
                style={{ width: "100%", marginTop: "22px" }}
              >
                Apply
              </Button>
            </Col>
          </Row>
        </Card>

        <div className="sales-dashboard-tabs">
          <Tabs
            activeKey={activeKey}
            onChange={onChange}
            type="card"
            size="small"
            tabBarStyle={{ marginBottom: "5px" }}
            items={allTabs.map((tab) => ({
              key: tab.key,
              label: tab.label,
              closable: false,
            }))}
          />
          <div className="tab-content">{renderTabContent()}</div>
        </div>
      </div>
    </div>
  );
};

export default AccountingTab;
